import React, { useState } from 'react';
import { X, ShoppingBag, MapPin, Package, Plus, Minus, AlertTriangle } from 'lucide-react';
import { trackFood, trackError } from '../Utils/analytics';
import './OrderCreation.css';

interface OrderCreationProps {
  foodItemId: number;
  foodItemName?: string;
  foodItemPrice?: number;
  onClose: () => void;
  onOrderSubmit: (foodItemId: number, deliveryAddress: string, quantity: number) => Promise<void>;
}

const OrderCreation: React.FC<OrderCreationProps> = ({
  foodItemId,
  foodItemName = 'Selected Item',
  foodItemPrice = 0,
  onClose,
  onOrderSubmit
}) => {
  console.log('OrderCreation: Component props received:');
  console.log('- foodItemId:', foodItemId);
  console.log('- foodItemName:', foodItemName);
  console.log('- foodItemPrice:', foodItemPrice);
  console.log('- foodItemPrice type:', typeof foodItemPrice);
  
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleQuantityChange = (change: number) => {
    const newQuantity = quantity + change;
    if (newQuantity >= 1 && newQuantity <= 10) {
      setQuantity(newQuantity);
    }
  };

  const calculateTotalPrice = () => {
    return (foodItemPrice * quantity).toFixed(2);
  };

  const validateForm = (): boolean => {
    if (!deliveryAddress.trim()) {
      setError('Please enter your delivery address');
      return false;
    }
    
    if (deliveryAddress.trim().length < 10) {
      setError('Please enter a complete delivery address (minimum 10 characters)');
      return false;
    }
    
    if (quantity < 1) {
      setError('Quantity must be at least 1');
      return false;
    }
    
    setError('');
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      trackError('order_creation_validation_failed', 'order_creation');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      await onOrderSubmit(foodItemId, deliveryAddress.trim(), quantity);
      trackFood('food_ordered', foodItemName);
      onClose();
    } catch (error: any) {
      console.error('Order submission error:', error);
      setError(error.message || 'Failed to place order. Please try again.');
      trackError('order_creation_submit_failed', 'order_creation');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="order-creation-overlay" onClick={handleOverlayClick}>
      <div className="order-creation-modal">
        {/* Header */}
        <div className="order-creation-header">
          <button 
            className="order-creation-close-button" 
            onClick={onClose}
            disabled={isSubmitting}
          >
            <X size={24} />
          </button>
          <h2 className="order-creation-title">Place Your Order</h2>
          <p className="order-creation-subtitle">
            Complete your order details below
          </p>
        </div>

        {/* Content */}
        <div className="order-creation-content">
          <form className="order-creation-form" onSubmit={handleSubmit}>
            {/* Order Summary */}
            <div className="order-summary-section">
              <h3 className="order-summary-title">
                <ShoppingBag size={20} />
                Order Summary
              </h3>
              <div className="order-summary-details">
                <div className="order-detail-item">
                  <span className="order-detail-label">Item</span>
                  <span className="order-detail-value">{foodItemName}</span>
                </div>
                <div className="order-detail-item">
                  <span className="order-detail-label">Unit Price</span>
                  <span className="order-detail-value price">₹{foodItemPrice.toFixed(2)}</span>
                </div>
                <div className="order-detail-item">
                  <span className="order-detail-label">Food Item ID</span>
                  <span className="order-detail-value">#{foodItemId}</span>
                </div>
                <div className="order-detail-item">
                  <span className="order-detail-label">Total Amount</span>
                  <span className="order-detail-value price">₹{calculateTotalPrice()}</span>
                </div>
              </div>
            </div>

            {/* Quantity Selection */}
            <div className="quantity-section">
              <h3 className="quantity-title">
                <Package size={20} />
                Quantity
              </h3>
              <div className="quantity-controls">
                <button
                  type="button"
                  className="quantity-button"
                  onClick={() => handleQuantityChange(-1)}
                  disabled={quantity <= 1 || isSubmitting}
                >
                  <Minus size={16} />
                </button>
                <div className="quantity-display">{quantity}</div>
                <button
                  type="button"
                  className="quantity-button"
                  onClick={() => handleQuantityChange(1)}
                  disabled={quantity >= 10 || isSubmitting}
                >
                  <Plus size={16} />
                </button>
              </div>
              <div className="total-price-display">
                <div className="total-price-label">Total Price</div>
                <div className="total-price-value">₹{calculateTotalPrice()}</div>
              </div>
            </div>

            {/* Delivery Address */}
            <div className="delivery-address-section">
              <h3 className="delivery-address-title">
                <MapPin size={20} />
                Delivery Address
              </h3>
              <textarea
                className={`delivery-address-input ${error ? 'error' : ''}`}
                value={deliveryAddress}
                onChange={(e) => {
                  setDeliveryAddress(e.target.value);
                  if (error) setError('');
                }}
                placeholder="Enter your complete delivery address including house number, street, area, city, and postal code..."
                rows={4}
                disabled={isSubmitting}
                required
              />
              <p className="delivery-address-help">
                Please provide a complete address including landmarks for accurate delivery.
                Example: "123 Main Street, Near Central Park, Downtown Area, Mumbai, 400001"
              </p>
            </div>

            {/* Error Display */}
            {error && (
              <div className="order-creation-error">
                <AlertTriangle className="order-creation-error-icon" size={20} />
                <span className="order-creation-error-text">{error}</span>
              </div>
            )}

            {/* Action Buttons */}
            <div className="order-creation-actions">
              <button
                type="button"
                className="order-creation-cancel-btn"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="order-creation-submit-btn"
                disabled={isSubmitting || !deliveryAddress.trim()}
              >
                {isSubmitting ? (
                  <>
                    <div className="order-creation-loading-spinner"></div>
                    <span>Placing Order...</span>
                  </>
                ) : (
                  <>
                    <ShoppingBag size={20} />
                    <span>Place Order - ₹{calculateTotalPrice()}</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default OrderCreation;